/**
 * Newsletter Module Constants
 * Shared constants used across the newsletter admin interface
 */
import ISO6391 from 'iso-639-1'
import { byIso } from 'country-code-lookup'

// Language options following the Localization module pattern
// Using ISO 639-1 language codes with native display names
// name = English name, displayName = Native name
export const LANGUAGE_OPTIONS = [
  // Western European
  { code: "en", name: "English", displayName: "English" },
  { code: "es", name: "Spanish", displayName: "Español" },
  { code: "fr", name: "French", displayName: "Français" },
  { code: "de", name: "German", displayName: "Deutsch" },
  { code: "it", name: "Italian", displayName: "Italiano" },
  { code: "pt", name: "Portuguese", displayName: "Português" },
  { code: "nl", name: "Dutch", displayName: "Nederlands" },
  { code: "sv", name: "Swedish", displayName: "Svenska" },
  { code: "da", name: "Danish", displayName: "Dansk" },
  { code: "no", name: "Norwegian", displayName: "Norsk" },
  { code: "fi", name: "Finnish", displayName: "Suomi" },
  
  // Eastern European & Slavic
  { code: "ru", name: "Russian", displayName: "Русский" },
  { code: "pl", name: "Polish", displayName: "Polski" },
  { code: "bg", name: "Bulgarian", displayName: "Български" },
  { code: "cs", name: "Czech", displayName: "Čeština" },
  { code: "sk", name: "Slovak", displayName: "Slovenčina" },
  { code: "sl", name: "Slovenian", displayName: "Slovenščina" },
  { code: "hr", name: "Croatian", displayName: "Hrvatski" },
  { code: "sr", name: "Serbian", displayName: "Српски" },
  { code: "uk", name: "Ukrainian", displayName: "Українська" },
  { code: "be", name: "Belarusian", displayName: "Беларуская" },
  { code: "mk", name: "Macedonian", displayName: "Македонски" },
  
  // Other European
  { code: "ro", name: "Romanian", displayName: "Română" },
  { code: "hu", name: "Hungarian", displayName: "Magyar" },
  { code: "et", name: "Estonian", displayName: "Eesti" },
  { code: "lv", name: "Latvian", displayName: "Latviešu" },
  { code: "lt", name: "Lithuanian", displayName: "Lietuvių" },
  { code: "mt", name: "Maltese", displayName: "Malti" },
  { code: "is", name: "Icelandic", displayName: "Íslenska" },
  { code: "ga", name: "Irish", displayName: "Gaeilge" },
  { code: "cy", name: "Welsh", displayName: "Cymraeg" },
  { code: "eu", name: "Basque", displayName: "Euskera" },
  { code: "ca", name: "Catalan", displayName: "Català" },
  { code: "gl", name: "Galician", displayName: "Galego" },
  { code: "tr", name: "Turkish", displayName: "Türkçe" },
  { code: "el", name: "Greek", displayName: "Ελληνικά" },
  
  // East Asian
  { code: "zh", name: "Chinese", displayName: "中文" },
  { code: "ja", name: "Japanese", displayName: "日本語" },
  { code: "ko", name: "Korean", displayName: "한국어" },
  
  // Middle Eastern & Semitic
  { code: "ar", name: "Arabic", displayName: "العربية" },
  { code: "he", name: "Hebrew", displayName: "עברית" },
  { code: "fa", name: "Persian", displayName: "فارسی" },
  { code: "ur", name: "Urdu", displayName: "اردو" },
  
  // South Asian
  { code: "hi", name: "Hindi", displayName: "हिन्दी" },
  { code: "bn", name: "Bengali", displayName: "বাংলা" },
  { code: "ta", name: "Tamil", displayName: "தமிழ்" },
  { code: "te", name: "Telugu", displayName: "తెలుగు" },
  { code: "ml", name: "Malayalam", displayName: "മലയാളം" },
  { code: "kn", name: "Kannada", displayName: "ಕನ್ನಡ" },
  { code: "gu", name: "Gujarati", displayName: "ગુજરાતી" },
  { code: "mr", name: "Marathi", displayName: "मराठी" },
  
  // Southeast Asian
  { code: "th", name: "Thai", displayName: "ไทย" },
  { code: "vi", name: "Vietnamese", displayName: "Tiếng Việt" },
  { code: "id", name: "Indonesian", displayName: "Bahasa Indonesia" },
  { code: "ms", name: "Malay", displayName: "Bahasa Melayu" },
  { code: "tl", name: "Filipino", displayName: "Filipino" },
  
  // African
  { code: "sw", name: "Swahili", displayName: "Kiswahili" },
  { code: "am", name: "Amharic", displayName: "አማርኛ" },
  { code: "zu", name: "Zulu", displayName: "isiZulu" },
  { code: "xh", name: "Xhosa", displayName: "isiXhosa" },
  { code: "af", name: "Afrikaans", displayName: "Afrikaans" },
]

// Subscriber status options
export const SUBSCRIBER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  UNSUBSCRIBED: 'unsubscribed',
  BOUNCED: 'bounced',
  COMPLAINED: 'complained'
}

// Subscriber types
export const SUBSCRIBER_TYPE = {
  REGISTERED: 'registered',
  ANONYMOUS: 'anonymous',
  IMPORTED: 'imported'
}

// Newsletter release status options
export const RELEASE_STATUS = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  RUNNING: 'running',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
  FAILED: 'failed'
}

// Email template types
export const TEMPLATE_TYPE = {
  NEWSLETTER: 'newsletter',
  WELCOME: 'welcome',
  CONFIRMATION: 'confirmation',
  UNSUBSCRIBE: 'unsubscribe'
}

// Default language code
export const DEFAULT_LANGUAGE = 'en'

// Maximum subscribers per import batch
export const MAX_IMPORT_BATCH_SIZE = 1000

// Analytics time periods
export const ANALYTICS_PERIODS = {
  LAST_7_DAYS: '7d',
  LAST_30_DAYS: '30d',
  LAST_90_DAYS: '90d',
  LAST_YEAR: '1y'
}

// Email delivery statuses
export const DELIVERY_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  BOUNCED: 'bounced',
  COMPLAINED: 'complained',
  UNSUBSCRIBED: 'unsubscribed',
  FAILED: 'failed'
}

// Map common country codes to language codes
const COUNTRY_TO_LANGUAGE = {
  'us': 'en',
  'usa': 'en',
  'uk': 'en',
  'gb': 'en',
  'au': 'en',
  'ca': 'en',
  'nz': 'en',
  'jp': 'ja',
  'cn': 'zh',
  'kr': 'ko',
  'mx': 'es',
  'br': 'pt',
  'ar': 'es',
  'cl': 'es',
  'co': 'es',
  'pe': 'es',
  've': 'es',
  'at': 'de',
  'ch': 'de',
  'be': 'nl',
  'in': 'hi',
  'pk': 'ur',
  'sa': 'ar',
  'ae': 'ar',
  'eg': 'ar',
  'il': 'he',
  'ir': 'fa',
  'th': 'th',
  'vn': 'vi',
  'id': 'id',
  'my': 'ms',
  'ph': 'tl',
  'sg': 'en',
  'hk': 'zh',
  'tw': 'zh',
  'za': 'en',
  'ke': 'sw',
  'ng': 'en'
}

// Language helper functions
export const getLanguageByCode = (code) => {
  if (!code) return null
  // Normalize code to lowercase for case-insensitive matching
  let normalizedCode = code.toLowerCase().trim()

  // If it's a country code, convert to language code
  if (normalizedCode.length === 2 && COUNTRY_TO_LANGUAGE[normalizedCode]) {
    normalizedCode = COUNTRY_TO_LANGUAGE[normalizedCode]
  }

  return LANGUAGE_OPTIONS.find(lang => lang.code.toLowerCase() === normalizedCode)
}

export const getLanguageDisplayName = (code) => {
  const language = getLanguageByCode(code)
  if (language) return language.displayName

  // Try converting country code to language code
  if (!code) return 'Unknown'
  let normalizedCode = code.toLowerCase().trim()
  if (COUNTRY_TO_LANGUAGE[normalizedCode]) {
    normalizedCode = COUNTRY_TO_LANGUAGE[normalizedCode]
  }

  // Fallback to iso-639-1 for native name
  const nativeName = ISO6391.getNativeName(normalizedCode)
  if (nativeName) return nativeName

  // Last resort: capitalize the original code with (Country Code) indicator
  return `${code.toUpperCase()} (Country)`
}

export const getLanguageName = (code) => {
  const language = getLanguageByCode(code)
  if (language) return language.name

  // Try converting country code to language code
  if (!code) return 'Unknown'
  let normalizedCode = code.toLowerCase().trim()
  if (COUNTRY_TO_LANGUAGE[normalizedCode]) {
    normalizedCode = COUNTRY_TO_LANGUAGE[normalizedCode]
  }

  // Fallback to iso-639-1 for English name
  const englishName = ISO6391.getName(normalizedCode)
  if (englishName) return englishName

  // Last resort: capitalize the original code with (Country Code) indicator
  return `${code.toUpperCase()} (Country)`
}

// Get English name for language code (e.g., "es" -> "Spanish")
export const getLanguageEnglishName = (code) => {
  return getLanguageName(code)
}

/**
 * Get language name - normalizes country codes to language codes first
 * This handles cases where country codes (US, GB, JP) are used instead of language codes (en, ja)
 * @param {string} code - Language code (en, es) or country code (US, GB, JP)
 * @returns {string} Language name in English
 */
export const getLanguageNameFromCode = (code) => {
  if (!code) return 'Unknown'

  let normalizedCode = code.toLowerCase().trim()

  // If it's a country code, convert to language code first
  if (COUNTRY_TO_LANGUAGE[normalizedCode]) {
    normalizedCode = COUNTRY_TO_LANGUAGE[normalizedCode]
  }

  // Check our custom LANGUAGE_OPTIONS
  const language = LANGUAGE_OPTIONS.find(lang => lang.code.toLowerCase() === normalizedCode)
  if (language) return language.name

  // Fallback to iso-639-1
  const englishName = ISO6391.getName(normalizedCode)
  if (englishName) return englishName

  // Last resort: show the code as uppercase
  return code.toUpperCase()
}

// Status helper functions
export const getStatusLabel = (status) => {
  const labels = {
    [SUBSCRIBER_STATUS.ACTIVE]: 'Active',
    [SUBSCRIBER_STATUS.INACTIVE]: 'Inactive',
    [SUBSCRIBER_STATUS.UNSUBSCRIBED]: 'Unsubscribed',
    [SUBSCRIBER_STATUS.BOUNCED]: 'Bounced',
    [SUBSCRIBER_STATUS.COMPLAINED]: 'Complained'
  }
  return labels[status] || status
}

export const getStatusVariant = (status) => {
  const variants = {
    [SUBSCRIBER_STATUS.ACTIVE]: 'admin_success',
    [SUBSCRIBER_STATUS.INACTIVE]: 'admin_neutral',
    [SUBSCRIBER_STATUS.UNSUBSCRIBED]: 'admin_warning',
    [SUBSCRIBER_STATUS.BOUNCED]: 'admin_error',
    [SUBSCRIBER_STATUS.COMPLAINED]: 'admin_error'
  }
  return variants[status] || 'admin_neutral'
}

export const getReleaseStatusLabel = (status) => {
  const labels = {
    [RELEASE_STATUS.DRAFT]: 'Draft',
    [RELEASE_STATUS.SCHEDULED]: 'Scheduled',
    [RELEASE_STATUS.RUNNING]: 'Running',
    [RELEASE_STATUS.COMPLETED]: 'Completed',
    [RELEASE_STATUS.PAUSED]: 'Paused',
    [RELEASE_STATUS.CANCELLED]: 'Cancelled',
    [RELEASE_STATUS.FAILED]: 'Failed'
  }
  return labels[status] || status
}

export const getReleaseStatusVariant = (status) => {
  const variants = {
    [RELEASE_STATUS.DRAFT]: 'admin_neutral',
    [RELEASE_STATUS.SCHEDULED]: 'admin_primary',
    [RELEASE_STATUS.RUNNING]: 'admin_success',
    [RELEASE_STATUS.COMPLETED]: 'admin_success',
    [RELEASE_STATUS.PAUSED]: 'admin_warning',
    [RELEASE_STATUS.CANCELLED]: 'admin_neutral',
    [RELEASE_STATUS.FAILED]: 'admin_error'
  }
  return variants[status] || 'admin_neutral'
}